'use client';

import { GoogleMap, Marker, MarkerClusterer, Polygon, useJsApiLoader } from '@react-google-maps/api';
import clsx from 'clsx';
import React, { memo } from 'react';
import { StoryblokStory } from 'storyblok-generate-ts';

import { useSelectedLocationStore } from '../../stores/SelectedLocationStore';

import { NEXT_PUBLIC_GOOGLE_MAPS_API_KEY, NEXT_PUBLIC_SITE_URL } from '@/config';
import { LocationStoryblok } from '@/types/storyblok/storyblok-types';

// import { googleMapStyle } from './mapStyle';

import styles from './Map.module.scss';

type MapProps = {
    locations: StoryblokStory<LocationStoryblok>[];
    isMuliLocationLayout: boolean;
    className?: string;
};

function convertStringToLatLngArray(stringifiedArray) {
    try {
        // Ensure it's a valid JSON string
        const cleanedString = stringifiedArray
            .replace(/\s+/g, '') // Remove whitespace
            .replace(/,]/g, ']') // Fix trailing commas
            .replace(/,}/g, '}'); // Fix trailing commas in objects

        const nestedArray = JSON.parse(cleanedString);

        // Ensure the array structure is correct
        if (!Array.isArray(nestedArray) || !Array.isArray(nestedArray[0]) || !Array.isArray(nestedArray[0][0])) {
            console.error('Invalid JSON format: Expected a nested array structure');
            return [];
        }

        return nestedArray.flat(2).map(coord => ({ lat: coord[1], lng: coord[0] }));
    } catch (error) {
        console.error('Invalid JSON string:', error);
        return [];
    }
}

export const Map: React.FC<MapProps> = memo(({ locations, isMuliLocationLayout, className }) => {
    const { setSelectedLocation } = useSelectedLocationStore();
    const locationPolygons = locations
        .filter(loc => loc.content.polygon)
        .map(loc => ({
            paths: convertStringToLatLngArray(loc.content.polygon),
            color: loc.content.color.color || '#FF0000',
            uuid: loc.uuid,
        }));

    const { isLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || 'AIzaSyAVsX32JudUA_bBL2tZZloYA3qrD5tmAJM',
    });

    const onLoad = React.useCallback(
        (m: google.maps.Map) => {
            m.setOptions({
                // styles: googleMapStyle,
                mapTypeControl: false,
                streetViewControl: false,
                fullscreenControl: false,
                minZoom: 10,
                maxZoom: 18,
            });

            // Build bounds for all markers
            const bounds = new window.google.maps.LatLngBounds();
            locations.forEach(story => {
                const { lat, lng } = story.content;
                bounds.extend({ lat: Number(lat), lng: Number(lng) });
            });
            // Extend bounds to include all polygons,
            locationPolygons.forEach(polygon => {
                polygon.paths.forEach(coord => {
                    bounds.extend(coord);
                });
            });
            // If we have markers, fit them within bounds
            if (!bounds.isEmpty()) {
                m.fitBounds(bounds, { top: -70, bottom: -70, left: -70, right: -70 });

                // Ensure the map is truly centered after fitBounds()
                setTimeout(() => {
                    const center = bounds.getCenter();
                    m.setCenter(center); // Force recenters map
                    const currentZoom = m.getZoom();
                    m.setOptions({
                        minZoom: Math.max(currentZoom - 1, 7), // Ensuring zoom is within limits
                    });
                }, 500);
            } else if (locations.length === 1) {
                const { lat, lng } = locations[0].content;
                m.setCenter({ lat: Number(lat), lng: Number(lng) });

                setTimeout(() => {
                    const currentZoom = m.getZoom();
                    m.setOptions({
                        minZoom: Math.max(currentZoom - 1, 7),
                    });
                }, 500);
            }
        },
        [locations, locationPolygons],
    );

    const onUnmount = React.useCallback((_: google.maps.Map) => {
        // setMap(null);
    }, []);

    if (!isLoaded) {
        return null;
    }
    return (
        <GoogleMap
            mapContainerStyle={{ borderRadius: 6 }}
            mapContainerClassName={clsx(
                styles.mapContainer,
                { [styles.mapContainerMultiLocation]: isMuliLocationLayout },
                className,
            )}
            onLoad={onLoad}
            onUnmount={onUnmount}
        >
            {locationPolygons.map(polygon => (
                <Polygon
                    key={polygon.uuid}
                    paths={polygon.paths}
                    onClick={() => {
                        setSelectedLocation(polygon.uuid);
                        const element = document.getElementById(polygon.uuid);
                        if (!element) return;
                        // Timeout is needed to properly position element into view after
                        setTimeout(() => {
                            element?.scrollIntoView({
                                behavior: 'smooth',
                                block: 'center',
                            });
                        }, 350);
                    }}
                    options={{
                        fillColor: polygon.color,
                        strokeColor: polygon.color,
                        strokeOpacity: 0.8,
                        strokeWeight: 2,
                        fillOpacity: 0.35,
                    }}
                />
            ))}
            <MarkerClusterer
                clusterClass={styles.clusterer}
                // options={{
                //     gridSize: 30, // Reduce clustering sensitivity
                //     zoomOnClick: true,
                //     // styles: [
                //     //     {
                //     //         url: '/assets/images/cluster-icon.png', // Custom small cluster icon
                //     //         height: 30, // Reduce size of cluster marker
                //     //         width: 30,
                //     //         textColor: '#ffffff',
                //     //         textSize: 10, // Smaller text inside clusters
                //     //     },
                //     // ],
                // }}
            >
                {clusterer => (
                    <>
                        {locations.map(story => {
                            const location = story.content;

                            return (
                                <Marker
                                    key={story.uuid}
                                    clusterer={clusterer}
                                    position={{ lat: Number(location.lat), lng: Number(location.lng) }}
                                    title={location.name}
                                    icon={`${NEXT_PUBLIC_SITE_URL}/assets/icons/pin.svg`}
                                    label={{
                                        text: location.name,
                                        className: styles.markerLabel,
                                        color: '#191D1F',
                                        fontSize: '20px',
                                        fontWeight: 'bold',
                                    }}
                                    onClick={() => {
                                        setSelectedLocation(story.uuid);
                                        const element = document.getElementById(story.uuid);
                                        if (!element) return;
                                        // Timeout is needed to properly position element into view after
                                        setTimeout(() => {
                                            element?.scrollIntoView({
                                                behavior: 'smooth',
                                                block: 'center',
                                            });
                                        }, 350);
                                    }}
                                />
                            );
                        })}
                    </>
                )}
            </MarkerClusterer>
        </GoogleMap>
    );
});
